import type { WorkBook, WorkSheet } from 'xlsx';
import type { SheetCell, SheetSpec } from './types.ts';

/**
 * Typed SheetJS Community Edition (`xlsx` 0.18.5, Apache-2.0) surface.
 *
 * The package's own `.d.ts` is full of `any`; this module is the only
 * place that talks to it, and every value we hand it is a `SheetCell`.
 * Dynamic `import('xlsx')` keeps the ~1.1 MB (min ~410 KB, gzip ~130 KB)
 * writer out of the initial console chunk — it loads on the first
 * export click.
 */

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export async function buildWorkbook(sheets: readonly SheetSpec[]): Promise<WorkBook> {
  const xlsx = await import('xlsx');
  const workbook: WorkBook = xlsx.utils.book_new();
  for (const sheet of sheets) {
    const aoa: SheetCell[][] = sheet.rows.map((row) => [...row]);
    const worksheet: WorkSheet = xlsx.utils.aoa_to_sheet(aoa);
    xlsx.utils.book_append_sheet(workbook, worksheet, sheet.name);
  }
  return workbook;
}

/**
 * Write a true `.xlsx` and trigger a browser download. Guarded for
 * non-DOM / object-URL-less environments (SSR, some test runners) so
 * an export click is inert there instead of throwing through the page.
 */
export async function downloadSheets(
  sheets: readonly SheetSpec[],
  filename: string,
): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (typeof URL.createObjectURL !== 'function' || typeof URL.revokeObjectURL !== 'function') {
    return;
  }
  const xlsx = await import('xlsx');
  const workbook = await buildWorkbook(sheets);
  const raw: unknown = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
  const buffer = arrayBufferFromWrite(raw);
  if (buffer === null) return;
  const blob = new Blob([buffer], { type: XLSX_MIME });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function arrayBufferFromWrite(raw: unknown): ArrayBuffer | null {
  if (raw instanceof ArrayBuffer) return raw;
  if (raw instanceof Uint8Array) {
    const buffer = new ArrayBuffer(raw.byteLength);
    new Uint8Array(buffer).set(raw);
    return buffer;
  }
  return null;
}
